import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '@/lib/utils';

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => {
  // Radix's popper wrapper sets min-width: max-content as an inline style,
  // which prevents max-w-xs/max-w-sm from constraining width and causes
  // tooltip text to render on a single line. Override via ref on mount.
  const callbackRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      if (node) {
        const wrapper = node.closest('[data-radix-popper-content-wrapper]') as HTMLElement | null;
        if (wrapper) wrapper.style.minWidth = '0';
      }
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
    },
    [ref]
  );

  return (
    <TooltipPrimitive.Content
      ref={callbackRef}
      sideOffset={sideOffset}
      className={cn(
        'dls-z-50 dls-overflow-hidden dls-whitespace-normal dls-break-words dls-rounded dls-border dls-bg-popover dls-px-3 dls-py-1.5 dls-text-sm dls-text-popover-foreground dls-shadow-10 dls-max-w-xs dls-animate-in dls-fade-in-0 dls-zoom-in-95 data-[state=closed]:dls-animate-out data-[state=closed]:dls-fade-out-0 data-[state=closed]:dls-zoom-out-95 data-[side=bottom]:dls-slide-in-from-top-2 data-[side=left]:dls-slide-in-from-right-2 data-[side=right]:dls-slide-in-from-left-2 data-[side=top]:dls-slide-in-from-bottom-2',
        className
      )}
      {...props}
    />
  );
});
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

const TooltipContentTitle = ({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
  <p className={cn('dls-text-sm dls-font-semibold', className)} {...props} />
);

const TooltipContentData = ({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
  <p className={cn('dls-text-sm dls-text-muted-foreground', className)} {...props} />
);

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, TooltipContentTitle, TooltipContentData };
